// Native (Tauri / macOS) capabilities for the Lisn desktop app.
//
// Everything here is a no-op in the browser — `isTauri()` gates every call,
// and the Tauri packages are loaded lazily so the web bundle never touches
// them. The desktop app uses these to capture system + mic audio natively
// (via the `lisn-audio` Swift sidecar) and to surface the mic-on notification.
//
// Design note: capture only happens natively. The captured audio is handed
// back as plain WAV Blobs, which the existing `/transcribe` pipeline ingests
// unchanged — so the whole analysis/suggestion/write-up flow is reused as-is.

export function isTauri() {
    return typeof window !== "undefined" && !!(window.__TAURI_INTERNALS__ || window.__TAURI__);
}

// The web origin where Google sign-in actually works (the hosted app). The
// desktop app sends the user here in the system browser to authenticate.
export const WEB_ORIGIN = "https://lisn.app";

// Tag the document so CSS can give the desktop app native chrome (thin
// scrollbars etc.) without affecting the website.
if (isTauri() && typeof document !== "undefined") {
    document.documentElement.classList.add("is-tauri");
}

/** Open the system browser (where Google OAuth is allowed) to sign in. */
export async function openDesktopLogin() {
    if (!isTauri()) return;
    const url = `${WEB_ORIGIN}/login?desktop=1`;
    try {
        const { open } = await import("@tauri-apps/plugin-shell");
        await open(url);
    } catch (e) {
        console.error("[lisn] shell.open failed, falling back", e);
        try { window.open(url, "_blank"); } catch { /* ignore */ }
    }
}

/**
 * Listen for the `lisn://auth?token=...` deep link the browser sends back
 * after sign-in. Calls back with the JWT. Also checks the launch URL in case
 * the app was opened cold by the link. Returns an unlisten fn.
 */
export async function onAuthToken(cb) {
    if (!isTauri()) return () => {};
    const dl = await import("@tauri-apps/plugin-deep-link");
    const handle = (urls) => {
        for (const u of urls || []) {
            try {
                const parsed = new URL(u);
                const token = parsed.searchParams.get("token");
                if (token) cb(token);
            } catch { /* ignore */ }
        }
    };
    try { handle(await dl.getCurrent()); } catch { /* none */ }
    try { return await dl.onOpenUrl(handle); } catch { return () => {}; }
}

// The HTTPS callback the desktop Drive OAuth redirects through. Must be a
// registered redirect URI on the Google OAuth client, and is passed to the
// backend so the token exchange uses the matching redirect_uri.
export const DRIVE_REDIRECT = `${WEB_ORIGIN}/drive-callback`;

/** Open the system browser to connect Google Drive (OAuth is blocked inside
 *  the macOS webview, same as the main login). The web handoff bounces the
 *  auth code back via the `lisn://drive?code=...` deep link. */
export async function openDriveConnect() {
    if (!isTauri()) return;
    const url = `${WEB_ORIGIN}/drive-connect?desktop=1`;
    try {
        const { open } = await import("@tauri-apps/plugin-shell");
        await open(url);
    } catch (e) {
        try { window.open(url, "_blank"); } catch { /* ignore */ }
    }
}

/** Listen for `lisn://drive?code=...` from the Drive OAuth handoff. Calls back
 *  with the one-time auth code. Returns an unlisten fn. */
export async function onDriveCode(cb) {
    if (!isTauri()) return () => {};
    const dl = await import("@tauri-apps/plugin-deep-link");
    const handle = (urls) => {
        for (const u of urls || []) {
            try {
                const parsed = new URL(u);
                if (parsed.host === "drive") {
                    const code = parsed.searchParams.get("code");
                    if (code) cb(code);
                }
            } catch { /* ignore */ }
        }
    };
    try { handle(await dl.getCurrent()); } catch { /* none */ }
    try { return await dl.onOpenUrl(handle); } catch { return () => {}; }
}

function b64ToBlob(b64, mime) {
    const bytes = atob(b64);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    return new Blob([arr], { type: mime || "audio/wav" });
}

/**
 * Spawn the native audio sidecar and stream chunks back as WAV Blobs.
 * @param {{ source: "system"|"mic"|"both", chunkMs?: number, onChunk: (blob: Blob) => void, onEvent?: (evt: object) => void }} opts
 * @returns {Promise<{ stop: () => Promise<void> }>}
 */
export async function startNativeCapture({ source = "both", chunkMs = 10000, silenceTimeoutMs, onChunk, onEvent }) {
    if (!isTauri()) throw new Error("native capture only available in the desktop app");
    const { Command } = await import("@tauri-apps/plugin-shell");
    const cmd = Command.sidecar("binaries/lisn-audio");
    let buffer = "";
    cmd.stdout.on("data", (line) => {
        // The shell plugin emits per-line, but be defensive about partials.
        buffer += line;
        let idx;
        while ((idx = buffer.indexOf("\n")) >= 0) {
            const raw = buffer.slice(0, idx).trim();
            buffer = buffer.slice(idx + 1);
            if (!raw) continue;
            let evt;
            try { evt = JSON.parse(raw); } catch { continue; }
            if (evt.event === "chunk" && evt.b64) {
                try { onChunk(b64ToBlob(evt.b64, evt.mime)); } catch { /* ignore */ }
            }
            onEvent?.(evt);
        }
    });
    cmd.stderr.on("data", (l) => console.warn("[lisn-audio]", l));
    const child = await cmd.spawn();
    const startMsg = { cmd: "start", source, chunkMs };
    if (typeof silenceTimeoutMs === "number") startMsg.silenceTimeoutMs = silenceTimeoutMs;
    await child.write(JSON.stringify(startMsg) + "\n");
    return {
        async stop() {
            try { await child.write(JSON.stringify({ cmd: "stop" }) + "\n"); } catch { /* ignore */ }
            try { await child.write(JSON.stringify({ cmd: "quit" }) + "\n"); } catch { /* ignore */ }
            try { await child.kill(); } catch { /* ignore */ }
        },
        async pause() { try { await child.write(JSON.stringify({ cmd: "pause" }) + "\n"); } catch { /* ignore */ } },
        async resume() { try { await child.write(JSON.stringify({ cmd: "resume" }) + "\n"); } catch { /* ignore */ } },
    };
}

// ─────────────────────────────────────────────────────────────────────────
// Floating companion window ("PiP"). A native always-on-top Tauri window that
// surfaces the live "worth asking" question while the user is in their meeting
// app — because Document Picture-in-Picture isn't available in the macOS
// WKWebView, this is the desktop equivalent. The main window drives it via
// events; the overlay sends button actions back the same way.
// ─────────────────────────────────────────────────────────────────────────

/** Get the pre-declared (hidden) `pip` window handle, or null. */
export async function getPipWindow() {
    if (!isTauri()) return null;
    try {
        const { WebviewWindow } = await import("@tauri-apps/api/webviewWindow");
        return (await WebviewWindow.getByLabel("pip")) || null;
    } catch { return null; }
}

/** Push current state to the companion window. */
export async function emitPipState(state) {
    if (!isTauri()) return;
    try {
        const { emit } = await import("@tauri-apps/api/event");
        await emit("pip:state", state);
    } catch { /* ignore */ }
}

/** Listen for button actions from the companion window. Returns an unlisten fn. */
export async function onPipAction(cb) {
    if (!isTauri()) return () => {};
    try {
        const { listen } = await import("@tauri-apps/api/event");
        return await listen("pip:action", (e) => { try { cb(e.payload); } catch { /* ignore */ } });
    } catch { return () => {}; }
}

/** Push a live audio level (0..1) to the companion window's waveform. */
export async function emitPipLevel(value) {
    if (!isTauri()) return;
    try {
        const { emit } = await import("@tauri-apps/api/event");
        await emit("pip:level", { value });
    } catch { /* ignore */ }
}

/** From inside the companion window: subscribe to live audio level. Returns unlisten. */
export async function onPipLevel(cb) {
    try {
        const { listen } = await import("@tauri-apps/api/event");
        return await listen("pip:level", (e) => { try { cb(e.payload?.value ?? 0); } catch { /* ignore */ } });
    } catch { return () => {}; }
}

/** From inside the companion window: send a button action to the main window. */
export async function sendPipAction(action) {
    try {
        const { emit } = await import("@tauri-apps/api/event");
        await emit("pip:action", { action });
    } catch { /* ignore */ }
}

/** From inside the companion window: subscribe to state pushes. Returns unlisten. */
export async function onPipState(cb) {
    try {
        const { listen } = await import("@tauri-apps/api/event");
        return await listen("pip:state", (e) => { try { cb(e.payload); } catch { /* ignore */ } });
    } catch { return () => {}; }
}

/** Start dragging the companion window (frameless windows need manual drag). */
export async function startPipDrag() {
    try {
        const { getCurrentWindow } = await import("@tauri-apps/api/window");
        await getCurrentWindow().startDragging();
    } catch { /* ignore */ }
}

/**
 * From inside the companion window: resize it and re-center it at the top of
 * the current monitor (Dynamic-Island position). Used to grow/shrink between
 * the collapsed pill and the expanded question panel.
 */
export async function setPipBounds(width, height, topGap = 0) {
    try {
        const { getCurrentWindow, currentMonitor } = await import("@tauri-apps/api/window");
        const { LogicalSize, LogicalPosition } = await import("@tauri-apps/api/dpi");
        const win = getCurrentWindow();
        await win.setSize(new LogicalSize(width, height));
        const mon = await currentMonitor();
        if (mon) {
            const sf = mon.scaleFactor || 1;
            const screenW = mon.size.width / sf;
            const screenX = (mon.position?.x || 0) / sf;
            await win.setPosition(new LogicalPosition(Math.round(screenX + (screenW - width) / 2), topGap));
        }
    } catch { /* best-effort */ }
}

/**
 * Watch whether *any* app starts using the microphone (e.g. a Zoom call) and
 * call back with the running state. Used to prompt "capture this meeting?".
 * @returns {Promise<{ stop: () => Promise<void> }>}
 */
export async function startMicMonitor(onMicActive) {
    if (!isTauri()) return { stop: async () => {} };
    const { Command } = await import("@tauri-apps/plugin-shell");
    const cmd = Command.sidecar("binaries/lisn-audio");
    let buffer = "";
    cmd.stdout.on("data", (line) => {
        buffer += line;
        let idx;
        while ((idx = buffer.indexOf("\n")) >= 0) {
            const raw = buffer.slice(0, idx).trim();
            buffer = buffer.slice(idx + 1);
            if (!raw) continue;
            try {
                const evt = JSON.parse(raw);
                if (evt.event === "mic-active") onMicActive(!!evt.running);
            } catch { /* ignore */ }
        }
    });
    const child = await cmd.spawn();
    await child.write(JSON.stringify({ cmd: "monitor" }) + "\n");
    return {
        async stop() { try { await child.write(JSON.stringify({ cmd: "quit" }) + "\n"); await child.kill(); } catch { /* ignore */ } },
    };
}

/**
 * Fire `cb` whenever the app window gains focus. Used so that tapping a
 * notification (which brings the app forward) can trigger an action — more
 * reliable on macOS than notification action buttons. Returns an unlisten fn.
 */
export async function onAppFocus(cb) {
    if (!isTauri()) return () => {};
    const cleanups = [];
    // 1) Tauri native focus event (fires when the app is activated, e.g. by a
    //    notification tap from the background).
    try {
        const { getCurrentWindow } = await import("@tauri-apps/api/window");
        const un = await getCurrentWindow().onFocusChanged(({ payload }) => { if (payload) cb(); });
        cleanups.push(un);
    } catch { /* ignore */ }
    // 2) DOM focus + visibility, as belt-and-suspenders (window was minimized,
    //    or the native event didn't deliver).
    const onFocus = () => cb();
    const onVis = () => { if (!document.hidden) cb(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    cleanups.push(() => window.removeEventListener("focus", onFocus));
    cleanups.push(() => document.removeEventListener("visibilitychange", onVis));
    return () => cleanups.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
}

/**
 * Native notification: "the mic just turned on — want Lisn to capture this
 * meeting?" with a CTA. `onCapture` fires when the user accepts.
 */
export async function notifyCaptureMeeting({ onCapture } = {}) {
    if (!isTauri()) return;
    try {
        const n = await import("@tauri-apps/plugin-notification");
        let granted = await n.isPermissionGranted();
        if (!granted) granted = (await n.requestPermission()) === "granted";
        if (!granted) return;

        // Action button where supported; falls back to a tap-to-open notice.
        try {
            await n.registerActionTypes([{
                id: "lisn-capture",
                actions: [{ id: "capture", title: "Capture this meeting" }],
            }]);
            await n.onAction((evt) => { if (evt?.actionId === "capture") onCapture?.(); });
        } catch { /* action types not supported on this OS build — notice only */ }

        await n.sendNotification({
            title: "Lisn",
            body: "Looks like a meeting started. Capture it and take notes?",
            actionTypeId: "lisn-capture",
        });
    } catch (e) {
        console.warn("[lisn] notify failed", e);
    }
}
